/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Day,
  Week,
  Month,
  Agenda,
  ScheduleComponent,
  Inject,
  Resize,
  DragAndDrop,
  ViewDirective,
  ViewsDirective,
  RenderCellEventArgs,
  View,
} from '@syncfusion/ej2-react-schedule';
import { useDispatch, useSelector } from 'react-redux';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';
import { isSafari } from 'react-device-detect';
import { RootState } from 'packages/blu-booking/src/store';
import { getConfig, setConfig } from '../../../utility_objects/WhiteCache';
import { DefaultCalendarSetSelectedDate } from '../store';
import { setPrintComponent } from '../../../store/Appointment';
import { CalendarEventTemplate } from '../../../UI/CalendarEventTemplate/CalendarEventTemplate';
import { CalendarEventQuickInfo } from '../../../UI/CalendarEventQuickInfo/CalendarEventQuickInfo';
import { appointmentFormPopupOpen } from '../../../utility_objects/AppointmentFormPopupOpen';
import { AppointmentDragDrop } from '../../../utility_objects/AppointmentDragDrop';
import { AppointmentResize } from '../../../utility_objects/AppointmentResize';
import { AppointmentRender } from '../../../utility_objects/AppointmentRender';
import { isOpen } from '../utility_objects/work-hours';
import {
  CellStatus,
  WorkHoursCellTemplate,
} from '../../../UI/WorkHoursCellTemplate/WorkHoursCellTemplate';
import {
  AppointmentMouseDown,
  AppointmentMouseUp,
} from '../../../utility_objects/AppointmentMouseUp';
import { HolidayInterface } from 'packages/blu-booking/src/interfaces/Holiday';
import { useSchedulerPersistence } from '../hooks/useSchedulerPersistence';
import { useSchedulerRefresh } from '../hooks/useSchedulerRefresh';
import { useWorkWeekColumns } from '../hooks/useWorkWeekColumns';
import { useSchedulerData } from '../hooks/useSchedulerData';
import { CalendarLoader } from '../../../UI/CalendarLoader/CalendarLoader';

interface SchedulerProps {
  calendarPointOfSale: any;
  shiftPointOfSale: any;
}

export const Scheduler = memo(function Scheduler({
  calendarPointOfSale,
  shiftPointOfSale,
}: SchedulerProps): JSX.Element {
  const componentRef = useRef();
  const dispatch = useDispatch();
  const scheduleObj = useRef<ScheduleComponent>(null);

  // ─── Redux state ───────────────────────────────────────────────

  const {
    selectedDate,
    view,
    currentView,
    selectedPointOfSale,
    selectedUsers,
    selectedStudi,
    selectedServices,
    zoom,
  } = useSelector((state: RootState) => state.bluBookingDefaultCalendar);

  const { sessionProfilo, focus10 } = getConfig();

  // ─── Data fetching ─────────────────────────────────────────────
  const { appointments, holidays, isLoadingCalendarOrHolidays } =
    useSchedulerData();
  useSchedulerRefresh(
    scheduleObj,
    appointments,
    holidays,
    isLoadingCalendarOrHolidays
  );

  const getCellStatus = useCallback(
    (date: Date, type: string): CellStatus => {
      const result = isOpen(date, type, calendarPointOfSale, shiftPointOfSale);
      if (result === 0) return { code: 0, color: 'var(--closed-color)' };
      if (result === 1)
        return { code: 1, color: 'var(--alt-background-color)' };
      return { code: 2, color: 'var(--base-foreground-color)' };
    },
    [calendarPointOfSale, shiftPointOfSale]
  );

  const cellTemplate = useCallback(
    (props: any) => (
      <WorkHoursCellTemplate
        date={props.date}
        type={props.type}
        groupIndex={props.groupIndex}
        getStatus={getCellStatus}
      />
    ),
    [getCellStatus]
  );

  // ─── Syncfusion lifecycle ──────────────────────────────────────
  useSchedulerPersistence(scheduleObj, selectedDate, focus10);
  useWorkWeekColumns(
    view,
    selectedDate,
    calendarPointOfSale,
    'default-schedule',
    isLoadingCalendarOrHolidays
  );

  const onActionComplete = (args) => {
    if (args.requestType === 'dateNavigate') {
      const schedulerInstance = scheduleObj.current;
      const newDate = schedulerInstance.selectedDate; // Ottieni la nuova data dal componente

      dispatch(DefaultCalendarSetSelectedDate(newDate));
    }
  };

  const getWorkDaysStartEndHours = useCallback(
    (args: RenderCellEventArgs) => {
      if (
        args.elementType === 'workCells' ||
        args.elementType === 'monthCells'
      ) {
        args.element.id =
          args.date !== null
            ? dayjs(args.date).format('DD/MM/YYYY HH:mm') ?? ''
            : '';
        args.element.addEventListener('mousedown', AppointmentMouseDown, {
          capture: true,
        });
        args.element.addEventListener(
          'mouseup',
          (event: any) => AppointmentMouseUp(event, 'default'),
          { capture: true }
        );

        // Usa i refs per avere sempre i dati più recenti
        // (evita stale closures catturate da Syncfusion)
        const result = isOpen(
          args.date,
          args.elementType,
          calendarPointOfSale,
          shiftPointOfSale
        );

        if (result === 0)
          (args.element as any).style.backgroundColor = 'var(--closed-color)';
        else if (result === 1)
          (args.element as any).style.backgroundColor =
            'var(--alt-background-color)';
        else
          (args.element as any).className =
            'e-work-cells e-alternate-cells e-work-hours';
      } else if (args.elementType === 'majorSlot')
        args.element.setAttribute('style', 'font-size:14px; font-weight: 500;');
    },
    [calendarPointOfSale, shiftPointOfSale] // Nessuna dipendenza: usa refs per i dati dinamici
  );
  // ─── Templates & config ───────────────────────────────────────
  function dateHeaderTemplate(props: any) {
    const date: any = dayjs(props.date);
    const dateFormated = date.format('ddd D');
    const mounthFormated = date.format('MMM');
    return (
      <div
        style={{
          width: '100%',
          textAlign: 'center',
          cursor: 'pointer',
          overflow: 'hidden',
        }}
        onClick={() => dispatch(DefaultCalendarSetSelectedDate(props.date))}
      >
        <Typography
          fontSize="14px"
          fontWeight="500"
          sx={{ color: 'unset' }}
        >{`${dateFormated.charAt(0).toUpperCase()}${dateFormated.slice(1)} ${
          date.date() === 1 && view !== 'Day'
            ? `${mounthFormated.charAt(0).toUpperCase()}${mounthFormated.slice(
                1
              )}`
            : ''
        }`}</Typography>
      </div>
    );
  }

  const getZoom = useCallback(() => {
    return {
      enable: true,
      interval: zoom?.interval,
      slotCount: zoom?.slotCount,
    };
  }, [zoom]);

  useEffect(() => {
    dispatch(setPrintComponent(componentRef.current));
  }, [componentRef, dispatch]);

  const getModules = useMemo(() => {
    if (!sessionProfilo.agendaModifica) return [Day, Week, Month, Agenda];
    return [Day, Week, Month, Agenda, Resize, DragAndDrop];
  }, [sessionProfilo.agendaModifica]);

  // ─── Render ───────────────────────────────────────────────────
  return (
    <Box
      id="default-schedule"
      ref={componentRef}
      height={'100%'}
      width={'100%'}
    >
      <CalendarLoader open={isLoadingCalendarOrHolidays} />
      <ScheduleComponent
        id="-default-schedule-persistence"
        ref={scheduleObj}
        width="100%"
        timeScale={getZoom()}
        height="100%"
        cssClass="default-schedule-cell-dimension"
        selectedDate={selectedDate}
        currentView={currentView as View}
        dateFormat="dd/MM/yyyy"
        locale="it"
        minDate={new Date(2000, 0, 1)}
        enablePersistence={true}
        actionComplete={onActionComplete}
        eventSettings={{
          // dataSource: [
          //   ...appointments,
          //   ...(holidays ?? []).map((holiday: HolidayInterface) => {
          //     const date = dayjs()
          //       .set('month', holiday.month - 1)
          //       .set('date', holiday.day);
          //     return {
          //       ...holiday,
          //       Id: holiday.id,
          //       StartTime: date.format('YYYY-MM-DDT00:00:00'),
          //       IsAllDay: true,
          //       Subject: holiday.name,
          //       EndTime: date.format('YYYY-MM-DDT23:59:59'),
          //       color: 'var(--base-color)',
          //     };
          //   }),
          // ],
          template: (props: any) => (
            <CalendarEventTemplate {...props} currentView={currentView} />
          ),
        }}
        popupOpen={(args) => {
          if (args.data.IsAllDay) {
            args.cancel = true;

            return;
          }
          appointmentFormPopupOpen(
            args,
            selectedUsers,
            selectedStudi,
            selectedServices,
            selectedPointOfSale?.id
          );
        }}
        dragStart={(args: any) => {
          if (args.data.IsAllDay) {
            args.cancel = true;
            return;
          }
        }}
        dragStop={(args: any) => {
          AppointmentDragDrop(args, appointments, 'default');
        }}
        resizeStop={(args: any) => {
          if (args.data.IsAllDay) return;

          AppointmentResize(args, appointments);
        }}
        dateHeaderTemplate={dateHeaderTemplate}
        firstDayOfWeek={1}
        renderCell={getWorkDaysStartEndHours}
        cellTemplate={cellTemplate}
        startHour="05:00"
        eventRendered={(args) => AppointmentRender(args)}
        workDays={[0, 1, 2, 3, 4, 5, 6]}
        quickInfoTemplates={
          {
            header: (_: any) => null,
            content: (props: any) => <CalendarEventQuickInfo {...props} />,
          } as any
        }
      >
        <ViewsDirective>
          <ViewDirective option="Day" interval={view === '3Days' ? 3 : 1} />
          <ViewDirective option="Week" />
          <ViewDirective option="Month" />
          <ViewDirective option="Agenda" />
        </ViewsDirective>
        <Inject services={getModules} />
      </ScheduleComponent>
    </Box>
  );
});
